import * as analytics from 'serviceWorker/db/analytics';
import { ChartDataEntity } from 'serviceWorker/db/analytics';
import { InspectionTemplateEntity } from 'serviceWorker/repository/inspectionTemplate/entity';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';
import type {
  ChartAdditionalParams,
  ChartRequest,
  ChartResult,
  ChartSeries,
} from 'shared/domain/analytics/chartModel';
import {
  FilterValues,
  Finances,
  InspectionChart,
  IssueChart,
  isIssueChart,
} from 'shared/types/analytics';
import { issueSource } from '../issues/issueSource';
import { getPublishedInspections } from '../inspections/getInspections';
import { inspectionTemplateSource } from '../inspectionTemplates/inspectionTemplateSource';
import { calculate, calculateInspections } from './charts';
import {
  calculateFinances,
  canReuseLastSavedChart,
  createChartDataEntity,
  getAnalyticsConfig,
} from './model';

function toChartResult(entity: ChartDataEntity): ChartResult {
  return {
    chart: entity.name,
    series: entity.series,
    filters: entity.filters,
    locale: entity.locale,
    additionalParams: entity.additionalParams,
    updatedAt: entity.updatedAt,
  };
}

async function calculateIssueChart(
  chart: IssueChart,
  filters: FilterValues,
  timezone: string,
  additionalParams: ChartAdditionalParams | undefined
): Promise<ChartSeries> {
  const issues = await issueSource.getActiveEntities();
  return calculate(chart, issues, filters, timezone, additionalParams);
}

async function calculateInspectionChart(
  chart: InspectionChart,
  filters: FilterValues,
  timezone: string,
  additionalParams: ChartAdditionalParams | undefined
): Promise<ChartSeries> {
  const [inspections, templates] = await Promise.all([
    getPublishedInspections(),
    inspectionTemplateSource.getAllEntities(),
  ]);
  const templateProcessById = templates.reduce<Record<string, { process: string }>>(
    (result, template: InspectionTemplateEntity) => {
      result[template._id] = { process: template.process };
      return result;
    },
    {}
  );
  return calculateInspections(chart, inspections, templateProcessById, filters, timezone, additionalParams);
}

/** The cache only saves recalculations: a failed write is logged, the chart is still answered. */
async function cacheChart(chartEntity: ChartDataEntity): Promise<void> {
  try {
    await analytics.addBatch([chartEntity]);
  } catch (error) {
    swLog('Could not cache chart', LogLevel.INFO, error, { query: { chart: chartEntity.name } });
  }
}

/**
 * The chart of the request: the cached one while it still matches (same
 * filters, params and locale, not older than the cache time), otherwise
 * calculated from local data and cached.
 */
export async function getChartResult({
  chart,
  filters,
  locale,
  additionalParams,
  forceUpdate,
}: ChartRequest): Promise<ChartResult> {
  const savedChart = await analytics.getOne(chart);
  if (canReuseLastSavedChart(savedChart, forceUpdate, locale, filters, additionalParams)) {
    return toChartResult(savedChart);
  }

  const { projectId, timezone } = await getAnalyticsConfig();
  const series = isIssueChart(chart)
    ? await calculateIssueChart(chart, filters, timezone, additionalParams)
    : await calculateInspectionChart(chart, filters, timezone, additionalParams);
  const chartEntity = createChartDataEntity(series, filters, chart, projectId, locale, additionalParams, Date.now());
  await cacheChart(chartEntity);

  return toChartResult(chartEntity);
}

/** Costs and fines per company, calculated from the active issues. */
export async function getFinancialData(): Promise<Finances> {
  return calculateFinances(await issueSource.getActiveEntities());
}
